import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet default marker icons (CRA bundling)
try {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });
} catch {}

const onlineIcon = L.divIcon({
  className: "",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  html: `<div style="position:relative;width:22px;height:22px;">
    <span style="position:absolute;inset:0;border-radius:9999px;background:#22C55E;opacity:0.35;animation:pulse-ring 1.6s ease-out infinite;"></span>
    <span style="position:absolute;inset:4px;border-radius:9999px;background:#22C55E;border:2px solid white;box-shadow:0 0 0 1px #1C4532;"></span>
  </div>`,
});

const offlineIcon = L.divIcon({
  className: "",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  html: `<div style="width:14px;height:14px;border-radius:9999px;background:#94A3B8;border:2px solid white;box-shadow:0 0 0 1px #475569;"></div>`,
});

const userIcon = L.divIcon({
  className: "",
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  html: `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:9999px;background:#D9734E;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.25);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  </div>`,
});

function Recenter({ center }) {
  const map = useMap();
  const lat = center?.[0];
  const lng = center?.[1];
  useEffect(() => {
    if (lat == null || lng == null) return;
    map.setView([lat, lng], map.getZoom());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);
  return null;
}

function AutoResize() {
  const map = useMap();
  const observerRef = useRef(null);
  useEffect(() => {
    const container = map.getContainer();
    observerRef.current = new ResizeObserver(() => { map.invalidateSize(); });
    observerRef.current.observe(container);
    return () => observerRef.current?.disconnect();
  }, [map]);
  return null;
}

function InvalidateOnShow() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 100);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

function eq(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }
  return false;
}

const MapView = React.memo(function MapView({
  center = [12.9716, 77.5946], // default Bengaluru
  height = 320,
  workers = [],
  userMarker = null,
  zoom = 13,
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-sm overflow-hidden border border-[#E2E0D9] relative" style={{ height }}>
      <MapContainer center={center} zoom={zoom} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          key="tiles"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CARTO'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <Recenter key="recenter" center={center} />
        <AutoResize key="resize" />
        <InvalidateOnShow />
        {userMarker && (
          <Marker position={userMarker} icon={userIcon}>
            <Popup>{t("map.youAreHere")}</Popup>
          </Marker>
        )}
        {workers.map((w) => (
          <Marker
            key={w.id}
            position={[w.lat, w.lng]}
            icon={w.is_online ? onlineIcon : offlineIcon}
          >
            <Popup>
              <div className="font-display font-medium text-base">{w.name}</div>
              <div className="text-xs text-[#646964]">₹{w.price} · ⭐ {w.rating?.toFixed?.(1) || "5.0"}</div>
              {w.is_online !== undefined && (
                <div className={`text-xs mt-1 font-medium ${w.is_online ? "text-[#22C55E]" : "text-[#94A3B8]"}`}>
                  {w.is_online ? "● " + t("map.onlineNow") : "○ " + t("map.offline")}
                </div>
              )}
              {w.distance_km !== undefined && (
                <div className="text-xs text-[#1C4532] mt-1">{w.distance_km + " " + t("map.kmAway")}</div>
              )}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}, (prev, next) => {
  return eq(prev.center, next.center) && prev.height === next.height && prev.zoom === next.zoom &&
         eq(prev.userMarker, next.userMarker) && prev.workers?.length === next.workers?.length &&
         prev.workers?.every((w, i) => w.id === next.workers[i]?.id && w.lat === next.workers[i]?.lat && w.lng === next.workers[i]?.lng && w.name === next.workers[i]?.name && w.price === next.workers[i]?.price && w.rating === next.workers[i]?.rating && w.is_online === next.workers[i]?.is_online);
});

export default MapView;
